/* The half of a product that is not for sale.
 *
 * Photography, names and copy live here; price, sizes and stock come from
 * Shopify through `@/store/catalogue.js`, and `shopifyHandle` is the seam
 * between the two. The shop's own products carry no images, and the cut-outs
 * the floor is built from are shot for this site alone — so the pictures could
 * not come from there even if they were in it.
 *
 * A slug is this site's URL; a handle is Shopify's. They are deliberately not
 * the same string: the URLs are ours to keep stable, and the handles are
 * whatever the shop was set up with. */

import poloRedFlat from '@/assets/figures/polo-red.webp';
import poloBlueFlat from '@/assets/figures/polo-blue.webp';
import shirtWhiteFlat from '@/assets/figures/shirt-white.webp';
import tanktopFlat from '@/assets/figures/tanktop.webp';
import shirtBlackFlat from '@/assets/figures/shirt-black.webp';

import poloRedDetail1 from '@/assets/products/polo-red-detail-1.webp';
import poloRedDetail2 from '@/assets/products/polo-red-detail-2.webp';
import poloRedWall from '@/assets/products/polo-red-wall.webp';
import poloBlueDetail from '@/assets/products/polo-blue-detail.webp';
import poloBlueWall from '@/assets/products/polo-blue-wall.webp';
import shirtWhiteWall from '@/assets/products/shirt-white-wall.webp';
import tanktopDetail from '@/assets/products/tanktop-detail.webp';
import tanktopWall from '@/assets/products/tanktop-wall.webp';
import shirtBlackDetail from '@/assets/products/shirt-black-detail.webp';
import shirtBlackWall from '@/assets/products/shirt-black-wall.webp';

/* How a garment fits is a property of the cut, not of the shop, so the three
   cuts carry their own advice and each product points at the one that applies
   to it. Wording is the brand's own, kept verbatim. */
const POLO_SIZE_INFO = {
  title: 'Größeninformation',
  lines: [
    'ihr könnt true to size gehen, wenn ihr es oversized wollt, geht eine große hoch',
    'bei Fragen hittet uns auf insta @amite.clo up!',
  ],
};

const SHIRT_SIZE_INFO = {
  title: 'Größeninformation',
  lines: [
    'wenn ihr slim fit fühlt, geht true to size oder sogar in die nächst kleinere Größe, '
      + 'das gleiche gilt für oversized in die andere Richtung',
    'bei Fragen hittet uns auf insta @amite.clo up!',
  ],
};

const TANKTOP_SIZE_INFO = {
  title: 'Größeninformation',
  lines: [
    'wenn ihr slim fit fühlt, geht true to size oder sogar in die nächst kleinere Größe',
    'bei Fragen hittet uns auf insta @amite.clo up!',
  ],
};

// The one block that is the same for everything.
const SHIPPING_INFO = {
  title: 'Versand',
  lines: ['innerhalb 2-4 Werktagen bei dir!'],
};

/* `flat` is the cut-out shot the person lies in on the floor; `shots` are the
   photographed ones, which keep their own backgrounds and so get framed. */
export const PRODUCTS = [
  {
    slug: 'polo-red',
    shopifyHandle: 'bordeaux-polo',
    name: 'polo rot',
    flat: { src: poloRedFlat, w: 343, h: 873, alt: 'Person im roten Polohemd, auf dem Teppich liegend' },
    shots: [
      { src: poloRedDetail1, w: 1400, h: 933, alt: 'Detailaufnahme des roten Polohemds' },
      { src: poloRedDetail2, w: 933, h: 1400, alt: 'Zweite Detailaufnahme des roten Polohemds' },
      { src: poloRedWall, w: 933, h: 1400, alt: 'Rotes Polohemd an der Wand' },
    ],
    info: ['100% Baumwolle', 'veredelt in Deutschland', 'Stick + Flockdruck'],
    sizeInfo: POLO_SIZE_INFO,
  },
  {
    slug: 'polo-blue',
    shopifyHandle: 'navy-polo',
    name: 'polo blau',
    flat: { src: poloBlueFlat, w: 286, h: 945, alt: 'Person im blauen Polohemd, auf dem Teppich liegend' },
    shots: [
      { src: poloBlueDetail, w: 1400, h: 933, alt: 'Detailaufnahme des blauen Polohemds' },
      { src: poloBlueWall, w: 933, h: 1400, alt: 'Blaues Polohemd an der Wand' },
    ],
    info: ['100% Baumwolle', 'veredelt in Deutschland', 'Stick + Flockdruck'],
    sizeInfo: POLO_SIZE_INFO,
  },
  {
    slug: 'shirt-white',
    shopifyHandle: 'weisses-shirt',
    name: 'shirt weiß',
    flat: { src: shirtWhiteFlat, w: 401, h: 841, alt: 'Person im weissen Shirt, auf dem Teppich liegend' },
    shots: [
      { src: shirtWhiteWall, w: 933, h: 1400, alt: 'Weisses Shirt an der Wand' },
    ],
    info: ['100% made in germany', '100% Baumwolle'],
    sizeInfo: SHIRT_SIZE_INFO,
  },
  {
    slug: 'tanktop',
    shopifyHandle: 'tantkops',
    name: 'tanktop',
    flat: { src: tanktopFlat, w: 357, h: 878, alt: 'Person im Tanktop, auf dem Teppich liegend' },
    shots: [
      { src: tanktopDetail, w: 1400, h: 933, alt: 'Detailaufnahme des Tanktops' },
      { src: tanktopWall, w: 933, h: 1400, alt: 'Tanktop an der Wand' },
    ],
    info: ['Stickveredelung in Deutschland'],
    sizeInfo: TANKTOP_SIZE_INFO,
  },
  {
    slug: 'shirt-black',
    shopifyHandle: 'schwarzes-shirt',
    name: 'shirt schwarz',
    flat: { src: shirtBlackFlat, w: 392, h: 700, alt: 'Person im schwarzen Shirt, auf dem Teppich liegend' },
    shots: [
      { src: shirtBlackDetail, w: 1400, h: 933, alt: 'Detailaufnahme des schwarzen Shirts' },
      { src: shirtBlackWall, w: 933, h: 1400, alt: 'Schwarzes Shirt an der Wand' },
    ],
    info: ['100% made in germany', '100% Baumwolle'],
    sizeInfo: SHIRT_SIZE_INFO,
  },
];

export function getProduct(slug) {
  return PRODUCTS.find((product) => product.slug === slug) || null;
}

/* The way back from a Shopify cart line, which knows the handle and nothing
   about this site's URLs, to the photograph and the name to draw it with. */
export function getProductByHandle(handle) {
  return PRODUCTS.find((product) => product.shopifyHandle === handle) || null;
}

/* Product info only appears where we actually have copy for it; the fit advice
   belongs to the cut, and shipping is the same for everything. */
export function getSections(product) {
  const sections = [];
  if (product.info.length) {
    sections.push({ title: 'Produktinformationen', lines: product.info });
  }
  sections.push(product.sizeInfo, SHIPPING_INFO);
  return sections;
}
